<script setup lang="ts">
import type { IconDefinition
} from '@fortawesome/duotone-regular-svg-icons'
import type { ToastVariant } from '~/stores/toasts'
import {
  ToastDescription,
  ToastProvider,
  ToastRoot,
  ToastTitle,
  ToastViewport
} from 'reka-ui'
import { storeToRefs } from 'pinia'
import {
  faCheckCircle,
  faExclamationCircle,
  faExclamationTriangle,
  faInfoCircle
} from '@fortawesome/duotone-regular-svg-icons'
import { useToastStore } from '~/stores/toasts'

const store = useToastStore()
const { toasts } = storeToRefs(store)

const variantIcons: Record<ToastVariant, IconDefinition> = {
  success: faCheckCircle,
  error: faExclamationCircle,
  warning: faExclamationTriangle,
  info: faInfoCircle,
}

const variantClasses: Record<ToastVariant, string> = {
  success: 'bg-green-pop text-green-60 border-green-20',
  error: 'bg-coral-20 text-coral-60 border-coral-40',
  warning: 'bg-yellow-pop text-black border-yellow-50',
  info: 'bg-seagreen-pop text-blue-60 border-blue-20',
}

const handleClose = (id: string) => setTimeout(() => store.remove(id), 200)
</script>

<template>
  <ToastProvider>
    <ToastRoot
      v-for="toast in toasts"
      :key="toast.id"
      :duration="toast.duration"
      class="data-[state=open]:animate-toast-in data-[state=closed]:animate-toast-out rounded-xl border py-3 pr-5 pl-4 shadow-2xl"
      :class="variantClasses[toast.variant]"
      @update:open="open => !open && handleClose(toast.id)"
    >
      <div class="flex gap-3">
        <font-awesome-icon
          :icon="variantIcons[toast.variant]"
          size="xl"
        />
        <div class="flex flex-col">
          <ToastTitle class="text-base font-bold">
            {{ toast.title }}
          </ToastTitle>
          <ToastDescription v-if="toast.message" class="text-sm font-medium">
            {{ toast.message }}
          </ToastDescription>
        </div>
      </div>
    </ToastRoot>

    <ToastViewport class="fixed right-0 bottom-0 z-100 flex max-w-134 min-w-40 flex-col items-end gap-2 p-(--viewport-padding) outline-none [--viewport-padding:_26px]" />
  </ToastProvider>
</template>
